import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import type { Action, ThunkAction } from '@reduxjs/toolkit';
import type { IAuthState, IRootState } from '@/store/types';
import type { PersistConfig } from 'redux-persist/es/types';
import type { AxiosRequestConfig } from 'axios';
import type { ApiResponse } from '@/api/types';
import reportSlice from './slices/reportSlice';

import {
  apiConfigReducer,
  authReducer,
  availabilityReducer,
  contractReducer,
  equipmentReducer,
  resourcesReducer,
  scheduleReducer,
  userReducer,
  utilsReducer,
  gigAdminReducer,
  gigCampaignAdminReducer,
  gigCountryAdminReducer,
  clientReducer,
  vocabReducer,
  agentPositionReducer,
  courseBuilderReducer,
  interviewsReducer,
  interviewsSPReducer,
  courseClassAdminReducer,
  gigPermissionAdminReducer,
  permissionClientAdminReducer,
  permissionAdminReducer,
  agentsAdminReducer,
  userPermissionAdminReducer,
  serviceUserPermissionAdminReducer,
  permissionGroupAdminReducer,
  groupPermissionAdminReducer,
  interviewQuestionReducer,
  voiceQuestionReducer,
  earningsReducer,
  classSliceReducer,
  fraudGrpReviewReducer,
  gigReferFriendReducer,
  referralProgramReducer,
  communityAdminReducer,
} from '@/store/slices';
import resumeBuilderSlice from './slices/resume-builder.slice';
import gigSlice from './slices/gigSlice';
import voiceScriptSlice from './slices/voiceScript.slice';
import skillsSlice from './slices/skillsSlice';
import { KnownErrors } from '@/store/enums';
import courseSlice from './slices/courseSlice';
import featureFlagSlice from './slices/featureFlags/featureFlagsSlice';
import campaignsSlice from './slices/su/campaignsSlice';
import campaignsLayoutSlice from './slices/su/campaignsLayoutSlice';
import applicationSlice from './slices/su/applicationSlice';
import applicantDetailsSlice from './slices/su/applicantDetailsSlice';
import classesSlice from './slices/su/classesSlice';
import classDetailsSlice from './slices/su/classDetailsSlice';
import notificationListSlice from './slices/su/notifyListSlice';
import createNotificationSlice from './slices/su/createNotificationSlice';
import getNotificationStatusSlice from './slices/su/getNotificationStatusSlice';
import applicantNotesSlice from '@/store/slices/su/applicantNotesSlice';
import masSlice from './slices/su/masSlice';
import spApplicationSlice from './slices/sp/spApplicationSlice';
import adSourceSlice from './slices/su/adSourceSlice';

const reducers = combineReducers({
  apiConfig: apiConfigReducer,
  auth: authReducer,
  availability: availabilityReducer,
  contract: contractReducer,
  user: userReducer,
  utils: utilsReducer,
  schedule: scheduleReducer,
  resources: resourcesReducer,
  resumeBuilder: resumeBuilderSlice,
  equipments: equipmentReducer,
  gigs: gigSlice,
  voiceScript: voiceScriptSlice,
  skills: skillsSlice,
  courses: courseSlice,
  reports: reportSlice,
  gigsAdmin: gigAdminReducer,
  gigCountryAdmin: gigCountryAdminReducer,
  client: clientReducer,
  vocab: vocabReducer,
  agentPosition: agentPositionReducer,
  featureFlags: featureFlagSlice,
  gigCampaignAdmin: gigCampaignAdminReducer,
  campaigns: campaignsSlice,
  campaignLayout: campaignsLayoutSlice,
  application: applicationSlice,
  applicantDetails: applicantDetailsSlice,
  applicantNotes: applicantNotesSlice,
  courseBuilder: courseBuilderReducer,
  classes: classesSlice,
  adSource: adSourceSlice,
  classDetails: classDetailsSlice,
  interviews: interviewsReducer,
  interviewsSP: interviewsSPReducer,
  courseClass: courseClassAdminReducer,
  notifications: notificationListSlice,
  createNotification: createNotificationSlice,
  notificationQueue: getNotificationStatusSlice,
  permissionAdmin: permissionAdminReducer,
  referAFriendSlice: gigReferFriendReducer,
  agentsAdmin: agentsAdminReducer,
  fraudGrpReview: fraudGrpReviewReducer,
  gigPermissionAdmin: gigPermissionAdminReducer,
  permissionClientAdmin: permissionClientAdminReducer,
  userPermissionAdmin: userPermissionAdminReducer,
  serviceUserPermissionAdmin: serviceUserPermissionAdminReducer,
  permissionGroupAdmin: permissionGroupAdminReducer,
  groupPermissionAdmin: groupPermissionAdminReducer,
  interviewQuestion: interviewQuestionReducer,
  voiceQuestion: voiceQuestionReducer,
  mas: masSlice,
  earnings: earningsReducer,
  spApplication: spApplicationSlice,
  class: classSliceReducer,
  referralProgram: referralProgramReducer,
  communityAdmin: communityAdminReducer,
});

const persistConfig: PersistConfig<IRootState, IRootState, IAuthState, IAuthState> = {
  key: 'root',
  storage,
  whitelist: ['gigs', 'classes', 'class'],
};

const persistedReducer = persistReducer(persistConfig, reducers);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'auth/authUser/fulfilled'],
      },
    }),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export type StandardRequest<Return = unknown, Payload = void> = Payload extends void | AxiosRequestConfig
  ? NonPayloadRequest<Return>
  : PayloadRequest<Return, Payload>;

export type NonPayloadRequest<Return> = (config?: AxiosRequestConfig) => ApiResponse<Return | KnownErrors>;
export type PayloadRequest<Return, Payload> = (
  payload: Payload,
  config?: AxiosRequestConfig,
) => ApiResponse<Return | KnownErrors>;

export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>;
